<script setup lang="ts">
import { storeToRefs } from 'pinia';

import useBuyNft from '@/composables/web3/useBuyNft.client';
import { CartStateEnum, useCartStore } from '@/stores/cart/useCartStore';
import { useTransactionStore } from '@/stores/transaction/useTransactionStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { watch } from '#imports';
import useEVMApi from '~/composables/useApi/useEVMApi';
import useEVMCore from '~/composables/web3/EVM/useEVMCore';
import type { ValidateOrderWithUuid } from '~/service/utils/validator/order/types';

type Props = {
    isMobile: boolean;
};

defineProps<Props>();

const evmCore = useEVMCore();

const { $provider } = useNuxtApp();

const {
    order
} = useEVMApi();

const { transactions } = storeToRefs(useTransactionStore());

const cartStore = useCartStore();

const { setCartState } = cartStore;

const {
    cart,
    cartState
} = storeToRefs(cartStore);

const allCartValues = computed(() => [...cart.value.values()]);

const userStore = useUserStore();

const { user } = storeToRefs(userStore);

const alertStore = useAlertsStore();

const {
    buyNFT
} = useBuyNft();

const isPaymentLoading = useState(() => false);

const { eventEmitter } = useEVMCore();

const handleToPayment = () => {
    if (user.value) {
        setCartState({ state: CartStateEnum.PAYMENT });
    }
    else {
        setCartState({ visible: false });
        userStore.showUserIsDisconnect();
    }
};

const attemptBuyNft = async () => {
    const resultTransactions = await buyNFT({ transactionType: 'purchase:cart' });
    if (resultTransactions && resultTransactions.length) {
        setCartState({ state: CartStateEnum.TRANSACTIONS });
    }
};

watch(
    [
        cartState,
        user
    ],
    ([
        stateValue,
        userValue
    ]) => {
        if (stateValue.state === CartStateEnum.PAYMENT && !userValue) {
            setCartState({ state: CartStateEnum.CART });
        }
    }
);

const updateCartByBestOrders = async () => {
    const uuids = allCartValues.value.map(item => item.uuid);
    const { data: responseOrders } = await order.bestOrders(uuids);

    const orderValidator = evmCore.createEvmOrderValidator($provider);

    const {
        withError,
        withoutError
    } = orderValidator.partitionOrders(responseOrders, allCartValues.value);

    const flattenedOrderList = Object.values(withoutError).flat();

    const withErrorOrders: ValidateOrderWithUuid[] = withError.map(item => ({ status: 'expired', uuid: item.uuid, message: item.error }));

    const validatedBestOrders = await orderValidator.validateBestOrder({
        items: allCartValues.value,
        orderList: flattenedOrderList
    });

    const validatedUuids = new Set(validatedBestOrders.map(item => item.uuid));

    const filteredWithErrorOrders = withErrorOrders.filter(
        item => !validatedUuids.has(item.uuid)
    );

    for (const validatedBest of [...validatedBestOrders, ...filteredWithErrorOrders]) {
        if (validatedBest?.status !== 'idle') {
            alertStore.add({
                title: 'Oops, something went wrong',
                description: validatedBest.message,
                critical: false,
                status: 'type.info'
            });
        }

        if (validatedBest?.status === 'expired') {
            cartStore.changeCartItem(validatedBest.uuid, { status: 'expired' });
        }

        else if (validatedBest?.status === 'return_best' && validatedBest.order.price?.amount) {
            cartStore.changeCartItem(validatedBest.uuid, {
                price: {
                    ...validatedBest.order.price,
                    amount: validatedBest.order.price?.amount
                },
                maxAmount: validatedBest.order.maxAmount
            });
        }
    }
};

const updateOrders = async () => {
    if (document.visibilityState === 'visible') {
        await updateCartByBestOrders();
    }
};

watch([allCartValues, () => cartState.value.visible], async ([newAllCartValues, newCartVisible], [_, oldCartVisible]) => {
    if ((newCartVisible && newCartVisible !== oldCartVisible) && newAllCartValues.length) {
        await updateOrders();
    }
});

onMounted(() => {
    eventEmitter.on('purchase:cart', {
        key: 'CartPayment',
        onSuccess: () => {
            isPaymentLoading.value = false;
        },
        onError: () => {
            isPaymentLoading.value = false;
        },
        onLoad: (loading) => {
            if (loading) isPaymentLoading.value = true;
        }
    });
    document.addEventListener('visibilitychange', updateOrders);
});

onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', updateOrders);
    eventEmitter.off('purchase:cart', 'CartPayment');
});

</script>

<template>
  <Dropdown :is-open="cartState.visible"
            :position="isMobile ? 'headerFixed' : 'bottomLeft'"
            @update:is-open="(val: boolean) => setCartState({ visible: val })"
  >
    <template #toggle>
      <ClientOnly>
        <template #fallback>
          <div type="button"
               role="button"
               aria-label="My cart"
               :title="cart.size > 0 ? 'Review your items' : 'Your cart is empty'"
               class="cart-button display-flex justify-center align-items-center border-500"
               :class="cartState.visible && 'async-cart-button--active'"
               @click="() => setCartState({ visible: !cartState.visible })"
          >
            <IconMain icon="cart"
                      :size="24"
            />
            <div v-if="cart.size"
                 class="cart-button__amount display-flex justify-center align-items-center"
            >
              {{ cart.size }}
            </div>
          </div>
        </template>
        <div type="button"
             role="button"
             aria-label="My cart"
             :title="cart.size > 0 ? 'Review your items' : 'Your cart is empty'"
             class="cart-button display-flex justify-center align-items-center border-500"
             :class="cartState.visible && 'async-cart-button--active'"
             @click="() => setCartState({ visible: !cartState.visible })"
        >
          <IconMain icon="cart"
                    :size="24"
          />
          <div v-if="cart.size"
               class="cart-button__amount display-flex justify-center align-items-center"
          >
            {{ cart.size }}
          </div>
        </div>
      </ClientOnly>
    </template>
    <template #content>
      <div class="cart-wrapper display-flex">
        <Transition name="slide-fade"
                    mode="out-in"
        >
          <div v-if="!cart.size && !transactions.length"
               class="grow width-full"
          >
            <CartEmptyState />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.CART"
               class="grow width-full"
          >
            <Cart @to-payment="handleToPayment" />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.PAYMENT"
               class="grow width-full"
          >
            <CartPayment :is-payment-loading="isPaymentLoading"
                         @to-cart="setCartState({ state: CartStateEnum.CART })"
                         @on-pay="attemptBuyNft()"
            />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.TRANSACTIONS"
               class="grow width-full"
          >
            <CartTransactions :is-payment-loading="isPaymentLoading" />
          </div>
          <div v-else-if="cartState.state === CartStateEnum.SUCCESS"
               class="grow width-full"
          >
            <CartSuccess />
          </div>
        </Transition>
      </div>
    </template>
  </Dropdown>
</template>

<style scoped lang="scss">

.async-cart-button--active {
  background-color: var(--bg-secondary);
}

.cart {
  &-button {
    position: relative;
    width: var(--control-500-size-single, 52px);
    height: var(--control-500-size-single, 52px);
    transition: background-color 0.3s ease;
    cursor: pointer;

    &__amount {
      position: absolute;
      top: -5px;
      right: -5px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: var(--bg-brand-aviation-primary);
    }
  }
}

.cart-wrapper {
  width: 348px;
  padding: $spacing-400 $spacing-600 $spacing-400 $spacing-600;
  border-radius: var(--background-500-radius, 4px);
  background-color: var(--layer-floor-2);

  @include tablet-768 {
    width: 100%;
    height: 100%;
    padding-right: $spacing-400;
    padding-left: $spacing-400;
    background-color: var(--layer-floor-1);
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(1, 0.8, 0.9, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(2px);
  opacity: 0;
}
</style>
